import { BaseFieldFilter, NativeFieldFilter } from 'module/api/filter/types';
import { Operation } from 'module/api/filter/types';
import { unreachable } from 'module/helpers/unreachable';
import { DEFAULT_DATE_FORMAT } from 'module/types/common';
import { DateField } from 'module/types/filter-field/field/filterable-field';
import {
  ApiRelativeDirection,
  DateFieldFilter,
  DateFieldFilterOperationType,
  RelativeDirection,
} from 'module/types/filter-field/filter/date';
import moment, { Moment } from 'moment';

const RelativeFormatSeparator = '-';
const RelativeTimeDuration = 'd';

enum RelativeDateValue {
  Today = 'today',
  Tomorrow = 'tomorrow',
  ThisWeek = 'this_week',
  LastWeek = 'last_week',
  NextWeek = 'next_week',
  ThisMonth = 'this_month',
  LastMonth = 'last_month',
  NextMonth = 'next_month',
  ThisQuarter = 'this_quarter',
  LastQuarter = 'last_quarter',
  ThisYear = 'this_year',
}

const toStatic = (date: Moment): string =>
  moment(date).format(DEFAULT_DATE_FORMAT);

const fromApiDirection = (
  direction: ApiRelativeDirection,
): RelativeDirection => {
  switch (direction) {
    case ApiRelativeDirection.After:
      return RelativeDirection.FromNow;
    case ApiRelativeDirection.Ago:
      return RelativeDirection.Ago;
    default:
      return unreachable(direction);
  }
};

const toApiDirection = (direction: RelativeDirection): ApiRelativeDirection => {
  switch (direction) {
    case RelativeDirection.Ago:
      return ApiRelativeDirection.Ago;
    case RelativeDirection.FromNow:
      return ApiRelativeDirection.After;
    default:
      return unreachable(direction);
  }
};

const toRelative = (
  days: number | string | undefined,
  direction: RelativeDirection,
): string => {
  return [
    JSON.stringify(days),
    RelativeTimeDuration,
    toApiDirection(direction),
  ].join(RelativeFormatSeparator);
};

const fromRelative = (value: string) => {
  return value.split(RelativeFormatSeparator);
};

const isRelative = (value = '') => {
  return value.includes(
    RelativeFormatSeparator + RelativeTimeDuration + RelativeFormatSeparator,
  );
};
const isDirectionAgo = (value = '') => value.includes(ApiRelativeDirection.Ago);

const toApiFormat = (filter: DateFieldFilter): BaseFieldFilter => {
  switch (filter.type) {
    case DateFieldFilterOperationType.IsEmpty:
      return {
        operation: Operation.IsEmpty,
        sourceType: filter.field.sourceType,
      };
    case DateFieldFilterOperationType.IsNotEmpty:
      return {
        operation: Operation.IsNotEmpty,
        sourceType: filter.field.sourceType,
      };
    case DateFieldFilterOperationType.IsBeforeToday:
      return {
        operation: Operation.LessThan,
        value: RelativeDateValue.Today,
        sourceType: filter.field.sourceType,
      };
    case DateFieldFilterOperationType.IsToday:
      return {
        operation: Operation.Value,
        value: RelativeDateValue.Today,
        sourceType: filter.field.sourceType,
      };
    case DateFieldFilterOperationType.IsTomorrow:
      return {
        operation: Operation.Value,
        value: RelativeDateValue.Tomorrow,
        sourceType: filter.field.sourceType,
      };
    case DateFieldFilterOperationType.IsThisWeek:
      return {
        operation: Operation.Value,
        value: RelativeDateValue.ThisWeek,
        sourceType: filter.field.sourceType,
      };
    case DateFieldFilterOperationType.IsLastWeek:
      return {
        operation: Operation.Value,
        value: RelativeDateValue.LastWeek,
        sourceType: filter.field.sourceType,
      };
    case DateFieldFilterOperationType.IsNextWeek:
      return {
        operation: Operation.Value,
        value: RelativeDateValue.NextWeek,
        sourceType: filter.field.sourceType,
      };
    case DateFieldFilterOperationType.IsThisMonth:
      return {
        operation: Operation.Value,
        value: RelativeDateValue.ThisMonth,
        sourceType: filter.field.sourceType,
      };
    case DateFieldFilterOperationType.IsLastMonth:
      return {
        operation: Operation.Value,
        value: RelativeDateValue.LastMonth,
        sourceType: filter.field.sourceType,
      };
    case DateFieldFilterOperationType.IsThisQuarter:
      return {
        operation: Operation.Value,
        value: RelativeDateValue.ThisQuarter,
        sourceType: filter.field.sourceType,
      };
    case DateFieldFilterOperationType.IsLastQuarter:
      return {
        operation: Operation.Value,
        value: RelativeDateValue.LastQuarter,
        sourceType: filter.field.sourceType,
      };
    case DateFieldFilterOperationType.IsThisYear:
      return {
        operation: Operation.Value,
        value: RelativeDateValue.ThisYear,
        sourceType: filter.field.sourceType,
      };
    case DateFieldFilterOperationType.IsAny:
      return {
        operation: Operation.Value,
        sourceType: filter.field.sourceType,
      };
    case DateFieldFilterOperationType.IsMoreThan:
      return {
        operation:
          filter.direction === RelativeDirection.Ago
            ? Operation.LessThan
            : Operation.GreaterThan,
        value: toRelative(filter.days, filter.direction),
        sourceType: filter.field.sourceType,
      };
    case DateFieldFilterOperationType.IsExactly:
      return {
        operation: Operation.Value,
        value: toRelative(filter.days, filter.direction),
        sourceType: filter.field.sourceType,
      };
    case DateFieldFilterOperationType.IsLessThan:
      return {
        operation:
          filter.direction === RelativeDirection.Ago
            ? Operation.GreaterThan
            : Operation.LessThan,
        value: toRelative(filter.days, filter.direction),
        sourceType: filter.field.sourceType,
      };
    case DateFieldFilterOperationType.IsAfter:
      return {
        operation: Operation.GreaterThan,
        value: filter.value == null ? 'null' : toStatic(filter.value),
        sourceType: filter.field.sourceType,
      };
    case DateFieldFilterOperationType.IsOn:
      return {
        operation: Operation.Value,
        value: filter.value == null ? 'null' : toStatic(filter.value),
        sourceType: filter.field.sourceType,
      };
    case DateFieldFilterOperationType.IsBefore:
      return {
        operation: Operation.LessThan,
        value: filter.value == null ? 'null' : toStatic(filter.value),
        sourceType: filter.field.sourceType,
      };
    default:
      return unreachable(filter);
  }
};

const toType = (
  operation: Operation,
  value: string | undefined,
): DateFieldFilterOperationType => {
  switch (operation) {
    case Operation.HasValue:
    case Operation.IsNotEmpty:
      return DateFieldFilterOperationType.IsNotEmpty;
    case Operation.IsEmpty:
    case Operation.IsNull:
      return DateFieldFilterOperationType.IsEmpty;
    case Operation.LessThan:
      return value === RelativeDateValue.Today
        ? DateFieldFilterOperationType.IsBeforeToday
        : isRelative(value)
          ? isDirectionAgo(value)
            ? DateFieldFilterOperationType.IsMoreThan
            : DateFieldFilterOperationType.IsLessThan
          : DateFieldFilterOperationType.IsBefore;
    case Operation.GreaterThan:
      return isRelative(value)
        ? isDirectionAgo(value)
          ? DateFieldFilterOperationType.IsLessThan
          : DateFieldFilterOperationType.IsMoreThan
        : DateFieldFilterOperationType.IsAfter;
    case Operation.Value:
      switch (value) {
        case RelativeDateValue.Today:
          return DateFieldFilterOperationType.IsToday;
        case RelativeDateValue.Tomorrow:
          return DateFieldFilterOperationType.IsTomorrow;
        case RelativeDateValue.ThisWeek:
          return DateFieldFilterOperationType.IsThisWeek;
        case RelativeDateValue.LastWeek:
          return DateFieldFilterOperationType.IsLastWeek;
        case RelativeDateValue.NextWeek:
          return DateFieldFilterOperationType.IsNextWeek;
        case RelativeDateValue.ThisMonth:
          return DateFieldFilterOperationType.IsThisMonth;
        case RelativeDateValue.LastMonth:
          return DateFieldFilterOperationType.IsLastMonth;
        case RelativeDateValue.ThisQuarter:
          return DateFieldFilterOperationType.IsThisQuarter;
        case RelativeDateValue.LastQuarter:
          return DateFieldFilterOperationType.IsLastQuarter;
        case RelativeDateValue.ThisYear:
          return DateFieldFilterOperationType.IsThisYear;
        default:
          return isRelative(value)
            ? DateFieldFilterOperationType.IsExactly
            : DateFieldFilterOperationType.IsOn;
      }
    case Operation.IsNot:
    case Operation.OneOf:
    case Operation.Contains:
    case Operation.NoneOf:
    default:
      return unreachable(operation as never);
  }
};

const parse = (
  filter: NativeFieldFilter,
  field: DateField,
): DateFieldFilter => {
  const type = toType(filter.operation, filter.value);
  const value = (filter.value ?? '') as string;

  switch (type) {
    case DateFieldFilterOperationType.IsEmpty:
    case DateFieldFilterOperationType.IsNotEmpty:
    case DateFieldFilterOperationType.IsBeforeToday:
    case DateFieldFilterOperationType.IsToday:
    case DateFieldFilterOperationType.IsTomorrow:
    case DateFieldFilterOperationType.IsThisWeek:
    case DateFieldFilterOperationType.IsLastWeek:
    case DateFieldFilterOperationType.IsNextWeek:
    case DateFieldFilterOperationType.IsThisMonth:
    case DateFieldFilterOperationType.IsLastMonth:
    case DateFieldFilterOperationType.IsThisQuarter:
    case DateFieldFilterOperationType.IsLastQuarter:
    case DateFieldFilterOperationType.IsThisYear:
    case DateFieldFilterOperationType.IsAny:
      return { field, type };
    case DateFieldFilterOperationType.IsMoreThan:
    case DateFieldFilterOperationType.IsExactly:
    case DateFieldFilterOperationType.IsLessThan: {
      const [days, _, direction] = fromRelative(value);

      return {
        field,
        type,
        days: JSON.parse(days),
        direction: fromApiDirection(direction as ApiRelativeDirection),
      };
    }
    case DateFieldFilterOperationType.IsAfter:
    case DateFieldFilterOperationType.IsOn:
    case DateFieldFilterOperationType.IsBefore: {
      const moment_value = JSON.parse(`"${value}"`) as string | null;

      return {
        field,
        type,
        value: moment_value == null ? undefined : moment(moment_value),
      };
    }
    default:
      return unreachable(type);
  }
};

export const DateFilter = {
  toApiFormat,
  parse,
};
